/**
 * CompanionSystem — Manages hireable named companions for Spellroads.
 *
 * Companions are named, persistent characters with defined personalities,
 * dialogue, and stats. Unlike generic hired NPCs, companions are unique
 * (only one instance can be hired at a time) and have weekly wages.
 *
 * Usage:
 *   CompanionSystem.init();
 *   CompanionSystem.getAvailableForHire(town)  → array of companion defs
 *   CompanionSystem.hire(id, party)             → { success, message }
 *   CompanionSystem.dismiss(id, party)          → { success, message }
 *   CompanionSystem.payWages(party)             → void
 *   CompanionSystem.getHiredCompanions()        → Character[]
 *
 * Debug:
 *   window.__debug.hireCompanion(id)  — hire companion for free
 *
 * Events emitted:
 *   companion:hired               { companionId, character }
 *   companion:dismissed           { companionId }
 *   companion:leftDueToUnpaidWages { companionId, name }
 *
 * @module CompanionSystem
 */

import DebugLog from '../debug/DebugLog.js';
import EventBus from '../core/EventBus.js';
import CharacterSystem from './CharacterSystem.js';
import WorldState from '../world/WorldState.js';
import COMPANIONS_DATA from '../data/companions.json';

// ─── CompanionSystem ──────────────────────────────────────────────────────────

const CompanionSystem = {
  /** @type {object[]} All companion definitions loaded from companions.json */
  _companions: [],

  /**
   * Map of companionId → character object for currently hired companions.
   * @type {Map<string, object>}
   */
  _hired: new Map(),

  /**
   * Map of companionId → number of days wages have been unpaid.
   * @type {Map<string, number>}
   */
  _unpaidDays: new Map(),

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  /**
   * Initialise the system. Loads companion definitions.
   * Must be called before any other method.
   */
  init() {
    this._companions = COMPANIONS_DATA ?? [];
    this._hired.clear();
    this._unpaidDays.clear();

    DebugLog.info(`[companion] CompanionSystem initialised; ${this._companions.length} companions loaded`);

    this._registerDebugAPI();
  },

  // ─── Queries ──────────────────────────────────────────────────────────────

  /**
   * Return companion definitions available for hire at a given town.
   * Returns 3 companions not already hired, filtered by town level.
   * Selection is deterministic per town name + current day.
   *
   * @param {{ name?: string, level?: number }} [town]
   * @returns {object[]} Array of up to 3 companion definition objects
   */
  getAvailableForHire(town = {}) {
    const townLevel = town.level ?? 1;
    const townName  = town.name  ?? 'unknown';

    // Filter: not hired, meet town level requirement
    const eligible = this._companions.filter(c => {
      if (this._hired.has(c.id)) return false;
      if ((c.minTownLevel ?? 1) > townLevel) return false;
      return true;
    });

    if (eligible.length === 0) return [];

    // Deterministic shuffle seeded by town name + current in-game day
    const day  = WorldState.currentDay ?? 1;
    const seed = (townName.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0) * 31 + day * 17) >>> 0;

    const shuffled = [...eligible].sort((a, b) => {
      const ha = (a.id.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0) * seed) % 1000;
      const hb = (b.id.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0) * seed) % 1000;
      return ha - hb;
    });

    return shuffled.slice(0, 3);
  },

  /**
   * Return all companion definitions (all 12).
   * @returns {object[]}
   */
  getAllDefinitions() {
    return this._companions;
  },

  /**
   * Look up a companion definition by ID.
   * @param {string} id
   * @returns {object|null}
   */
  getDefinition(id) {
    return this._companions.find(c => c.id === id) ?? null;
  },

  /**
   * Return an array of character objects for all currently hired companions.
   * @returns {object[]}
   */
  getHiredCompanions() {
    return [...this._hired.values()];
  },

  /**
   * Return the character object for a specific hired companion, or null.
   * @param {string} companionId
   * @returns {object|null}
   */
  getHiredCharacter(companionId) {
    return this._hired.get(companionId) ?? null;
  },

  /**
   * Whether a companion is currently hired.
   * @param {string} companionId
   * @returns {boolean}
   */
  isHired(companionId) {
    return this._hired.has(companionId);
  },

  // ─── Actions ──────────────────────────────────────────────────────────────

  /**
   * Hire a companion.
   * Deducts hireCost from WorldState.partyGold, creates a character via
   * CharacterSystem, adds to party (up to 4 members), and emits event.
   *
   * @param {string} companionId
   * @param {object[]} [party] — Current party array (WorldState.party by default)
   * @param {{ free?: boolean }} [opts] — Pass { free: true } to skip gold cost
   * @returns {{ success: boolean, message: string }}
   */
  hire(companionId, party, opts = {}) {
    const def = this.getDefinition(companionId);
    if (!def) {
      return { success: false, message: `Unknown companion id: "${companionId}".` };
    }

    if (this._hired.has(companionId)) {
      return { success: false, message: `${def.name} is already in your party.` };
    }

    const currentParty = party ?? WorldState.party ?? [];

    if (currentParty.length >= 4) {
      return { success: false, message: 'Party is full (maximum 4 members).' };
    }

    const cost = opts.free ? 0 : (def.hireCost ?? 0);

    if (!opts.free) {
      if ((WorldState.partyGold ?? 0) < cost) {
        return {
          success: false,
          message: `Insufficient gold. Need ${cost}g, have ${WorldState.partyGold ?? 0}g.`,
        };
      }
      WorldState.partyGold = (WorldState.partyGold ?? 0) - cost;
    }

    // Build character via CharacterSystem
    const character = CharacterSystem.createHero({
      name:     def.name,
      raceId:   def.race ?? 'human',
      attributeOverrides: def.attributes ?? null,
      skillTrees: def.skillTrees ?? [],
    });

    // Stamp companion-specific fields
    character.isHero        = false;
    character.isCompanion   = true;
    character.companionId   = companionId;
    character.personality   = def.personality ?? 'loyal';
    character.wage          = def.weeklyWage ?? 10;
    character.weeklyWage    = def.weeklyWage ?? 10;
    character.portrait      = def.portrait ?? null;
    character.dialogue      = def.dialogue ?? [];
    character.level         = def.startingLevel ?? 1;

    // Recompute HP/mana for the correct starting level
    character.maxHP   = CharacterSystem.computeMaxHP(character);
    character.hp      = character.maxHP;
    character.maxMana = CharacterSystem.computeMaxMana(character);
    character.mana    = character.maxMana;

    this._hired.set(companionId, character);
    this._unpaidDays.set(companionId, 0);

    // Add to party array
    const activeParty = WorldState.party ?? [];
    if (!activeParty.includes(character)) {
      activeParty.push(character);
      WorldState.party = activeParty;
    }

    DebugLog.info(
      `[companion] Hired; id=${companionId} name=${def.name} ` +
      `cost=${cost}g wage=${def.weeklyWage}g/week`
    );

    EventBus.emit('companion:hired', { companionId, character });

    return {
      success: true,
      message: `${def.name} has joined your party for ${cost}g. Weekly wage: ${def.weeklyWage}g.`,
    };
  },

  /**
   * Dismiss a companion from the party. No gold refund.
   *
   * @param {string} companionId
   * @param {object[]} [party]
   * @returns {{ success: boolean, message: string }}
   */
  dismiss(companionId, party) {
    const character = this._hired.get(companionId);
    if (!character) {
      return { success: false, message: 'Companion is not in your party.' };
    }

    const def = this.getDefinition(companionId);
    const name = def?.name ?? character.name ?? 'Companion';

    // Remove from party array
    const activeParty = party ?? WorldState.party ?? [];
    const idx = activeParty.indexOf(character);
    if (idx !== -1) {
      activeParty.splice(idx, 1);
      WorldState.party = activeParty;
    }

    this._hired.delete(companionId);
    this._unpaidDays.delete(companionId);

    DebugLog.info(`[companion] Dismissed; id=${companionId} name=${name}`);
    EventBus.emit('companion:dismissed', { companionId });

    return { success: true, message: `${name} has been dismissed.` };
  },

  /**
   * Deduct weekly wages for all hired companions.
   * Should be called once per in-game week (7 days).
   * Companions with wages unpaid for 7+ cumulative days have a 10% chance
   * per additional day of leaving on their own.
   *
   * @param {object[]} [party]
   */
  payWages(party) {
    const activeParty = party ?? WorldState.party ?? [];
    const totalWage   = this._getTotalWeeklyWage();

    if ((WorldState.partyGold ?? 0) >= totalWage) {
      // Pay everyone
      WorldState.partyGold = (WorldState.partyGold ?? 0) - totalWage;

      for (const [id] of this._hired) {
        this._unpaidDays.set(id, 0);
      }

      DebugLog.info(`[companion] Weekly wages paid; total=${totalWage}g`);
    } else {
      // Can't afford wages — mark days unpaid, companions may leave
      DebugLog.warn(`[companion] Cannot afford weekly wages (need ${totalWage}g, have ${WorldState.partyGold ?? 0}g)`);

      const leaving = [];
      for (const [id, character] of this._hired) {
        const days = (this._unpaidDays.get(id) ?? 0) + 7;
        this._unpaidDays.set(id, days);

        if (days >= 7) {
          // 10% chance per day past due
          const daysOverdue = days - 7;
          const leaveChance = Math.min(0.9, daysOverdue * 0.10);
          if (Math.random() < leaveChance) {
            leaving.push(id);
          }
        }
      }

      for (const id of leaving) {
        const character = this._hired.get(id);
        const def = this.getDefinition(id);
        const name = def?.name ?? character?.name ?? 'Companion';

        DebugLog.warn(`[companion] ${name} left due to unpaid wages`);
        EventBus.emit('companion:leftDueToUnpaidWages', { companionId: id, name });
        this.dismiss(id, activeParty);
      }
    }
  },

  /**
   * Compute the total weekly wage owed to all hired companions.
   * @returns {number}
   */
  _getTotalWeeklyWage() {
    let total = 0;
    for (const [id] of this._hired) {
      const def = this.getDefinition(id);
      total += def?.weeklyWage ?? 0;
    }
    return total;
  },

  // ─── Debug API ────────────────────────────────────────────────────────────

  /**
   * Register debug helpers on window.__debug.
   */
  _registerDebugAPI() {
    if (typeof window === 'undefined') return;

    window.__debug = window.__debug ?? {};

    /**
     * Hire a companion for free (debug only).
     * @param {string} id — Companion ID from companions.json
     * @returns {{ success: boolean, message: string }}
     */
    window.__debug.hireCompanion = (id) => {
      if (!id) {
        const defs = this.getAllDefinitions();
        console.table(defs.map(d => ({ id: d.id, name: d.name, race: d.race, cost: d.hireCost })));
        return { success: false, message: 'Provide a companion id. See table above.' };
      }

      const result = this.hire(id, WorldState.party ?? [], { free: true });
      DebugLog.info(`[debug] hireCompanion(${id}): ${result.message}`);
      return result;
    };

    /**
     * Dismiss a hired companion (debug only).
     * @param {string} id
     */
    window.__debug.dismissCompanion = (id) => {
      const result = this.dismiss(id, WorldState.party ?? []);
      DebugLog.info(`[debug] dismissCompanion(${id}): ${result.message}`);
      return result;
    };

    /**
     * List all hired companions (debug only).
     */
    window.__debug.listCompanions = () => {
      const hired = this.getHiredCompanions();
      if (hired.length === 0) {
        console.log('[CompanionSystem] No companions hired.');
        return [];
      }
      console.table(hired.map(c => ({
        companionId: c.companionId,
        name: c.name,
        level: c.level,
        hp: `${c.hp}/${c.maxHP}`,
        wage: `${c.weeklyWage}g/week`,
      })));
      return hired;
    };
  },
};

// ─── Module export ────────────────────────────────────────────────────────────

export default CompanionSystem;
